import type { BandPin } from '@tunasong/schemas'

/**
 * Band pins (GH #2822, spec docs/ux/2026-10-25-band-pins-ux.md): the Pinned shelf on a band's Home, stored on the
 * band space entity. Every function returns the same array when nothing changes, so a caller can skip the write.
 */

/**
 * Hard cap on pins, personal (the dashboard) and band alike: one rule to learn. Three is a deliberate Hick's-law
 * commitment — a pin is a strong signal, not a bookmark.
 */
export const PIN_CAP = 3

export type BandPinOutcome = 'pinned' | 'already-pinned' | 'full'

/** The shelf in order, numbered from 0 */
export const sortedBandPins = (pins: readonly BandPin[] = []): BandPin[] => [...pins].sort((a, b) => a.order - b.order)

const renumber = (pins: BandPin[]) => pins.map((pin, order) => (pin.order === order ? pin : { ...pin, order }))

export const isBandPinned = (pins: readonly BandPin[] | undefined, entityId: string) =>
  (pins ?? []).some(pin => pin.entityId === entityId)

/** Pin an item to the end of the shelf, unless it is already there or the shelf is full */
export const applyBandPin = (
  pins: BandPin[] | undefined,
  { entityId, pinnedBy }: { entityId: string; pinnedBy: string }
): { pins: BandPin[]; outcome: BandPinOutcome } => {
  const current = pins ?? []
  if (isBandPinned(current, entityId)) {
    return { pins: current, outcome: 'already-pinned' }
  }
  if (current.length >= PIN_CAP) {
    return { pins: current, outcome: 'full' }
  }
  return {
    pins: [...renumber(sortedBandPins(current)), { entityId, pinnedBy, order: current.length }],
    outcome: 'pinned',
  }
}

/** Take an item off the shelf; the rest keep their order */
export const applyBandUnpin = (pins: BandPin[] | undefined, entityId: string): BandPin[] => {
  const current = pins ?? []
  if (!isBandPinned(current, entityId)) {
    return current
  }
  return renumber(sortedBandPins(current).filter(pin => pin.entityId !== entityId))
}

/** Undo an unpin: the pin goes back at its old place, with its pinner, if there is still room */
export const restoreBandPin = (pins: BandPin[] | undefined, removed: BandPin): BandPin[] => {
  const current = pins ?? []
  if (isBandPinned(current, removed.entityId) || current.length >= PIN_CAP) {
    return current
  }
  const shelf = sortedBandPins(current)
  shelf.splice(Math.min(removed.order, shelf.length), 0, removed)
  return renumber(shelf)
}

/** Move an item to a new place on the shelf */
export const reorderBandPins = (pins: BandPin[] | undefined, entityId: string, toIndex: number): BandPin[] => {
  const shelf = sortedBandPins(pins)
  const from = shelf.findIndex(pin => pin.entityId === entityId)
  if (from < 0) {
    return pins ?? []
  }
  const [moved] = shelf.splice(from, 1)
  shelf.splice(Math.max(0, Math.min(toIndex, shelf.length)), 0, moved)
  return renumber(shelf)
}

/**
 * Re-apply what I did (from `base` to `mine`) to the server's pins now (`theirs`), for a save that met a newer
 * version (412). Two members pinning at once both keep their pins:
 *   - my unpins and my pins are applied to theirs; a pin they already made stays theirs;
 *   - a pin that would go over the cap is dropped, so the caller can say the shelf is full;
 *   - with nothing added or removed, my new order applies to the items we both have, and theirs-only items follow.
 */
export const mergeBandPins = ({
  base,
  theirs,
  mine,
}: {
  base: BandPin[] | undefined
  theirs: BandPin[] | undefined
  mine: BandPin[] | undefined
}): BandPin[] => {
  const baseIds = new Set((base ?? []).map(pin => pin.entityId))
  const mineSorted = sortedBandPins(mine)
  const mineIds = new Set(mineSorted.map(pin => pin.entityId))
  const removed = [...baseIds].filter(id => !mineIds.has(id))
  const added = mineSorted.filter(pin => !baseIds.has(pin.entityId))

  let merged = sortedBandPins(theirs)
  for (const entityId of removed) {
    merged = applyBandUnpin(merged, entityId)
  }
  for (const pin of added) {
    merged = applyBandPin(merged, pin).pins
  }
  if (removed.length > 0 || added.length > 0) {
    return renumber(sortedBandPins(merged))
  }
  /** A reorder: my order for the items we share, then the ones only they have */
  const shared = mineSorted.map(pin => merged.find(candidate => candidate.entityId === pin.entityId))
  const ordered = [
    ...shared.filter((pin): pin is BandPin => Boolean(pin)),
    ...merged.filter(pin => !mineIds.has(pin.entityId)),
  ]
  return renumber(ordered)
}
